import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { RevertOutput } from "@dramapie/shared";
import { useState } from "react";

import { listProjectHistory, revertProject } from "../../lib/api";
import { errorMessage } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import {
  formatBytes,
  HISTORY_SOURCE_META,
  latestRev,
  sortHistoryDesc,
} from "../../lib/m4";
import { EmptyState, ErrorState, LoadingState } from "../states";

export interface HistoryPaneProps {
  projectId: string;
  /** 当前故事板 rev：作为回滚的 `baseRev`，也用来标「当前」。 */
  currentRev: number | null;
  /** 回滚成功：由页面统一刷新 view + toast。 */
  onReverted: (output: RevertOutput, toRev: number) => void;
}

/**
 * 历史面板（右栏第 4 个 tab）。
 *
 * 每条 rev 都来自 `GET /history`（倒序）；「回滚到此版本」是**新提交**，
 * 不是删历史——所以二次确认里必须把这件事说清楚（契约 M4 §1）。
 *
 * 注：`HistoryItem` 没有 `path` 字段，所以这里只能整项目回滚
 * （`RevertInput.paths` 省略 = 整个项目回滚，服务端会先做快照，可再撤销）。
 */
export function HistoryPane({ projectId, currentRev, onReverted }: HistoryPaneProps) {
  const queryClient = useQueryClient();
  const [confirmRev, setConfirmRev] = useState<number | null>(null);
  const [reverting, setReverting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const historyQuery = useQuery({
    queryKey: ["history", projectId],
    queryFn: () => listProjectHistory(projectId, { limit: 60 }),
    enabled: projectId.length > 0,
    // 历史不是实时数据：tab 切回来时别每次都重取。
    staleTime: 5_000,
    retry: false,
  });

  const items = sortHistoryDesc(historyQuery.data?.items ?? []);
  const baseRev = currentRev ?? latestRev(items) ?? 0;

  async function handleRevert(toRev: number) {
    if (reverting !== null) return;
    setReverting(toRev);
    setError(null);
    try {
      const output = await revertProject(projectId, { baseRev, toRev });
      setConfirmRev(null);
      onReverted(output, toRev);
      await queryClient.invalidateQueries({ queryKey: ["history", projectId] });
    } catch (revertError) {
      setError(errorMessage(revertError, "回滚失败，请稍后重试"));
    } finally {
      setReverting(null);
    }
  }

  if (historyQuery.isPending) {
    return (
      <div className="dp-history-pane dp-scroll">
        <LoadingState label="正在读取版本历史…" />
      </div>
    );
  }

  if (historyQuery.isError) {
    return (
      <div className="dp-history-pane dp-scroll">
        <ErrorState
          title="版本历史加载失败"
          message={errorMessage(historyQuery.error, "版本历史加载失败")}
          onRetry={() => void historyQuery.refetch()}
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="dp-history-pane dp-scroll">
        <EmptyState
          title="还没有版本记录"
          description="每次提交（界面改动 / Agent 执行 / 导入）都会在这里留下一个 rev。"
        />
      </div>
    );
  }

  return (
    <div className="dp-history-pane dp-scroll">
      <p className="dp-data-note">
        每次提交都是一个 rev。回滚 = <b>产生一个新的 rev</b>，历史不会被删除，可以再撤销回来。
      </p>

      {error ? (
        <p role="alert" className="dp-history-error">
          {error}
        </p>
      ) : null}

      <ol className="dp-history-list">
        {items.map((item) => {
          const source = HISTORY_SOURCE_META[item.source];
          const isCurrent = currentRev !== null && item.rev === currentRev;
          const confirming = confirmRev === item.rev;
          return (
            <li
              key={`${item.rev}-${item.sha256}`}
              className="dp-history-item"
              data-current={isCurrent ? "true" : undefined}
            >
              <div className="dp-history-head">
                <span className="dp-history-rev">r{item.rev}</span>
                <span className="dp-history-time" title={formatDateTime(item.createdAt)}>
                  {formatDateTime(item.createdAt)}
                </span>
                <span className={`dp-history-source is-${item.source}`} title={`来源：${source.label}`}>
                  {source.code}
                </span>
              </div>

              <div className="dp-history-meta">
                <span className="dp-history-actor" title={item.actor}>
                  {item.actor}
                </span>
                <span className="dp-history-bytes">{formatBytes(item.bytes)}</span>
                {isCurrent ? <span className="dp-history-now">当前</span> : null}
              </div>

              {confirming ? (
                <div
                  role="group"
                  className="dp-history-confirm"
                  aria-label={`确认回滚到 rev ${item.rev}`}
                >
                  <p>
                    回滚到 <b>rev {item.rev}</b> 会把整个项目恢复到那个版本，并
                    <b>产生一个新版本</b>（rev {baseRev} → 新 rev）。
                    历史记录不会被删除，之后还能再撤销回来。
                  </p>
                  <div className="dp-history-confirm-actions">
                    <button
                      type="button"
                      className="dp-btn sm"
                      onClick={() => setConfirmRev(null)}
                      disabled={reverting !== null}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="dp-btn sm primary"
                      onClick={() => void handleRevert(item.rev)}
                      disabled={reverting !== null}
                    >
                      {reverting === item.rev ? "回滚中…" : "确认回滚"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="dp-history-actions">
                  <button
                    type="button"
                    className="dp-btn sm"
                    aria-label={`回滚到 rev ${item.rev}`}
                    onClick={() => {
                      setError(null);
                      setConfirmRev(item.rev);
                    }}
                    disabled={reverting !== null || isCurrent}
                    title={
                      isCurrent
                        ? "这已经是当前版本"
                        : `把整个项目回滚到 rev ${item.rev}（会产生新版本）`
                    }
                  >
                    回滚到此版本
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
